if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.loop = false;
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time':
            const countdownData = this.dataset.dueDate;
            if (countdownData) {
              this.targetTime = new Date(countdownData).getTime();
            }
            break;
          default: // Evergreen.
            this.targetTime = this.getEvergreenDueDate().getTime();
            break;
        }

        if (!this.targetTime) {
          return;
        }

        this.startTime = Date.now();
        if (this.targetTime > this.startTime || this.duration === 'every_wednesday') {
          this.fetchDomElements();
          this.beginCountdown();
        }
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        const currentTime = Date.now();
        
        // Check if the target time is valid
        if (!this.targetTime || this.targetTime <= currentTime) {
          if (this.duration === 'every_wednesday') {
            // Recalculate for the next Wednesday in case the current time expired
            this.targetTime = this.getEvergreenDueDate().getTime();
          } else {
            // If still not valid, don't start the countdown
            return;
          }
        }
        
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();
          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);
        this.classList.remove('hidden');
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);
        if (this.loop) {
          this.beginCountdown();
        } else if (this.duration === 'every_wednesday') {
          // For Wednesday timer, automatically reset for the next Wednesday
          this.targetTime = this.getEvergreenDueDate().getTime();
          if (this.targetTime > Date.now()) {
            this.beginCountdown();
          } else {
            this.classList.add('hidden');
          }
        } else {
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = '00';
          }
        });
      }

      getEvergreenDueDate() {
        let intervalHrs;
        const now = new Date();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_wednesday':
            // Wednesday is day 3 (0-indexed, 0 = Sunday, 3 = Wednesday)
            const dayOfWeek = now.getDay();
            let daysUntilWednesday;
            
            if (dayOfWeek < 3) {
              // If current day is before Wednesday (Sun-Tue)
              daysUntilWednesday = 3 - dayOfWeek;
            } else if (dayOfWeek > 3) {
              // If current day is after Wednesday (Thu-Sat)
              daysUntilWednesday = 10 - dayOfWeek; // 7 + (3 - dayOfWeek)
            } else {
              // If current day is Wednesday
              // If it's before 11:59:59 PM on Wednesday, count down to 11:59:59 PM today
              // Otherwise, count down to next Wednesday
              if (now.getHours() < 23 || (now.getHours() === 23 && (now.getMinutes() < 59 || (now.getMinutes() === 59 && now.getSeconds() < 59)))) {
                daysUntilWednesday = 0; // Same Wednesday, count down to 11:59:59 PM
              } else {
                daysUntilWednesday = 7; // Next Wednesday if we're at or past 11:59:59 PM
              }
            }
            
            dueDate.setDate(now.getDate() + daysUntilWednesday);
            dueDate.setHours(23, 59, 59, 0); // Set to 11:59:59 PM
            return dueDate;
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
            dueDate.setDate(1); // Start from the 1st day of the next month.
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week':
            const weekDayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - weekDayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return dueDate;
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }
    }
  );
}
